/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Article, H } from '@hzdg/sectioning';
import BlockContent from '@sanity/block-content-to-react';
import React, { Fragment } from 'react';

import { Box, Button, Flex, IntrinsicImage, Link, Text } from '..';
import { useLocalizedLink } from '../../../rockefellercenter/src/utils/use-localized-link';

import type { Block } from '@sanity/block-content-to-react';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

export type Ticket = {
  readonly includedFeatures?: readonly string[] | undefined;
  readonly price?: string | undefined;
  readonly title?: string | undefined;
  readonly description?: unknown;
  readonly id?: string | undefined;
  readonly image?: {
    readonly alt?: string | undefined;
    readonly asset?:
      | {
          readonly gatsbyImageData?: IGatsbyImageData;
        }
      | undefined;
  };
  readonly tertiaryUrl?:
    | {
        readonly caption?: string | undefined;
        readonly url?: string | undefined;
      }
    | undefined;
  readonly url?: {
    readonly caption?: string | undefined;
    readonly url?: string | undefined;
  };
};

export interface TicketCarouselCardProps {
  /**
   * MaxWidth of card
   */
  maxWidth?: Array<string | number> | number | string;
  /**
   * Ratio of card
   */
  ratio?: Array<number> | number;
  /** The ticket. */
  ticket: Ticket;
}

/**
 * `TicketCarouselCard` displays details about a Tishman attraction
 * ticket or package in a 'card' format. It can be used as an item
 * in a Carousel, or by itself.
 *
 * @see https://tishman.netlify.app/components/carousel-cards#ticketcarouselcard
 * @see https://tishman.netlify.app/components/carousel
 */
export function TicketCarouselCard({
  ticket,
}: TicketCarouselCardProps): JSX.Element {
  const getLocalizedLink = useLocalizedLink();

  return ticket ? (
    <Flex
      as={Article}
      sx={{
        border: '1px solid',
        borderColor: 'accent',
        flexDirection: 'column',
        gap: 3,
        height: '100%',
        justifyContent: 'space-between',
        maxWidth: ['calc(260 / 322 * 100vw)', '320px', null, '356px'],
        minHeight: [null, null, '600px'],
        minWidth: '257px',
        padding: 3,
      }}
    >
      <Box>
        {ticket.image && ticket.image.asset?.gatsbyImageData && (
          <IntrinsicImage
            // a11y: image is decorative if no alt text provided
            alt={ticket.image.alt?.replace('N/A', '')}
            image={ticket.image.asset?.gatsbyImageData}
            ratio={[234 / 128, 290 / 262]}
          />
        )}

        <H sx={{ variant: 'text.ticketTitleBase' }}>{ticket.title}</H>
        <Text variant="ticketDescriptionBase">
          {ticket.description && typeof ticket.description === 'object' ? (
            <BlockContent blocks={ticket.description as Block | Block[]} />
          ) : (
            <React.Fragment>
              {ticket.description as unknown as string}
            </React.Fragment>
          )}
        </Text>
      </Box>
      {ticket.tertiaryUrl?.url ? (
        <Link
          href={getLocalizedLink(ticket.tertiaryUrl.url)}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mx: 'auto',
          }}
          variant="underline"
        >
          {ticket.tertiaryUrl.caption}
        </Link>
      ) : null}
      {ticket.url?.url ? (
        <Link
          href={getLocalizedLink(ticket.url.url)}
          sx={{
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'py': 3,
            'px': 2,
            'bg': 'text',
            'color': 'background',
            'fontFamily': 'body',
            'fontSize': 1,
            'borderRadius': 'square',
            'letterSpacing': 4,
            'border': `1px solid`,
            'borderColor': 'text',
            'textTransform': 'uppercase',
            'textDecoration': 'none',
            'cursor': 'pointer',
            'transition': `color 0.3s ease-in-out,
                  background-color 0.3s ease-in-out,
                  border-color 0.3s ease-in-out`,

            ':not(:disabled):hover': {
              bg: 'accent',
              color: 'text',
            },

            ':disabled': {
              border: 'none',
              cursor: 'default',
              bg: 'backgroundDisabled',
            },
          }}
        >
          <Text as="span" variant="ticketButtonLabel">
            {ticket.url.caption}
          </Text>
          <Text as="span" mx={2} variant="ticketPrice">
            {ticket.price}
          </Text>
        </Link>
      ) : (
        <Button disabled py="18px">
          {ticket.url?.caption}
        </Button>
      )}
    </Flex>
  ) : (
    <Fragment />
  );
}
